import React, { Component, createRef } from "react";
import { Card, Button, Table, Badge, Form } from "react-bootstrap";

export default class BankStatementLineList extends Component {
  fileRef = createRef();

  triggerFile = () => this.fileRef.current && this.fileRef.current.click();

  // ---- helpers ----
  getFirst = (obj, keys) => {
    for (const k of keys) {
      if (
        obj &&
        Object.prototype.hasOwnProperty.call(obj, k) &&
        obj[k] != null
      ) {
        const v = String(obj[k]).trim();
        if (v) return v;
      }
    }
    return "";
  };

  toIsoDate = (v) => {
    if (!v) return "";
    const s = String(v).trim();
    let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
    if (m)
      return `${m[1].padStart(4, "0")}-${m[2].padStart(2, "0")}-${m[3].padStart(
        2,
        "0"
      )}`;
    m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (m) {
      let [, d, mo, y] = m;
      if (y.length === 2) y = (Number(y) < 50 ? "20" : "19") + y;
      return `${y.padStart(4, "0")}-${mo.padStart(2, "0")}-${d.padStart(
        2,
        "0"
      )}`;
    }
    const dt = new Date(s);
    if (!isNaN(dt.getTime())) {
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    return "";
  };

  parseAmount = (v) => {
    if (v == null) return 0;
    let s = String(v).trim();
    let neg = false;
    const paren = s.match(/^\((.*)\)$/);
    if (paren) {
      s = paren[1];
      neg = true;
    }
    s = s.replace(/[₹$,]/g, "").replace(/\s+/g, "");
    const tail = s.match(/^(-?\d+(?:\.\d+)?)(cr|dr)$/i);
    if (tail)
      return /dr/i.test(tail[2]) ? -parseFloat(tail[1]) : parseFloat(tail[1]);
    const n = parseFloat(s);
    if (isNaN(n)) return 0;
    return neg ? -n : n;
  };

  normalizeSide = (raw, signed) => {
    const s = String(raw || "")
      .trim()
      .toLowerCase();
    if (["credit", "cr", "c", "cr."].includes(s)) return "credit";
    if (["debit", "dr", "d", "dr."].includes(s)) return "debit";
    if (Number.isFinite(signed)) return signed < 0 ? "debit" : "credit";
    return "";
  };

  formatMoney = (amt) => {
    const cur = this.props.currency || "INR";
    const num = typeof amt === "number" ? amt : Number(amt || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: cur,
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return num.toFixed(2);
    }
  };

  formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${String(
      dt.getFullYear()
    ).slice(-2)} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
  };

  parseCsv(csvText) {
    const lines = csvText
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split("\n");
    if (!lines.length) return [];
    const header = this.splitCsvLine(lines[0]).map((h) => (h || "").trim());
    const out = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i]) continue;
      const cols = this.splitCsvLine(lines[i]);
      if (cols.length === 1 && cols[0].trim() === "") continue;
      const row = {};
      header.forEach((h, idx) => (row[h] = cols[idx] ?? ""));
      out.push(row);
    }
    return out;
  }

  splitCsvLine(line) {
    const res = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQuotes = !inQuotes;
      } else if (ch === "," && !inQuotes) {
        res.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    res.push(cur);
    return res.map((c) => c.trim());
  }

  dateKey = (x) => {
    const v = x.txnDate || x.date || x.TxnDate || x.Date || "";
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : 0;
  };

  // ---- CSV import ----
  onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = this.parseCsv(text);

    const normalized = rows.map((r, idx) => {
      const rawDate = this.getFirst(r, [
        "txnDate",
        "TxnDate",
        "Txn Date",
        "date",
        "Date",
        "valueDate",
        "ValueDate",
        "Value Date",
        "transactionDate",
        "Transaction Date",
        "postingDate",
        "Posting Date",
      ]);
      const isoDate = this.toIsoDate(rawDate);

      const rawAmt = this.getFirst(r, ["amount", "Amount", "Amt", "Value"]);
      const signedAmt = this.parseAmount(rawAmt);

      const sideRaw = this.getFirst(r, [
        "debitCredit",
        "DebitCredit",
        "creditOrDebit",
        "CreditOrDebit",
        "CRDR",
        "DrCr",
        "DRCR",
      ]);
      const side = this.normalizeSide(sideRaw, signedAmt);

      const particulars = this.getFirst(r, [
        "particulars",
        "Particulars",
        "description",
        "Description",
        "Narration",
        "Details",
      ]);

      return {
        id: r.id || r.Id || `csv-${idx}`,
        date: isoDate || (rawDate || "").slice(0, 10),
        particulars,
        amount: Math.abs(Number.isFinite(signedAmt) ? signedAmt : 0),
        creditOrDebit: side,
        txnDate: isoDate,
        debitCredit: side,
      };
    });

    normalized.sort(
      (a, b) =>
        this.dateKey(a) - this.dateKey(b) ||
        String(a.particulars || "").localeCompare(String(b.particulars || ""))
    );

    this.props.onCsvLoaded && this.props.onCsvLoaded(normalized);
    e.target.value = "";
  };

  render() {
    const {
      lines = [],
      onClear,
      selectedIds = new Set(),
      onToggle,
      isRowMatched = () => false,
    } = this.props;

    // Totals for statements
    const totals = (lines || []).reduce(
      (acc, e) => {
        const amt = Math.abs(Number(e.amount || 0));
        const side = (e.creditOrDebit || e.debitCredit || "")
          .toString()
          .trim()
          .toLowerCase();
        if (side === "credit") acc.cr += amt;
        else if (side === "debit") acc.dr += amt;
        else acc.unk += amt;
        return acc;
      },
      { cr: 0, dr: 0, unk: 0 }
    );
    const net = totals.cr - totals.dr;

    return (
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span className="fw-semibold">Bank Statement Lines (CSV)</span>
          <div className="d-flex gap-2">
            <input
              type="file"
              accept=".csv,text/csv"
              ref={this.fileRef}
              onChange={this.onFileChosen}
              style={{ display: "none" }}
            />
            <Button size="sm" onClick={this.triggerFile}>
              Load Bank Statement From CSV
            </Button>
            <Button size="sm" variant="outline-danger" onClick={onClear}>
              Clear
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <Table striped hover responsive className="mb-0">
            <thead>
              <tr>
                <th style={{ width: 36 }}></th>
                <th style={{ width: 130 }}>Date</th>
                <th>Particulars</th>
                <th className="text-end" style={{ width: 140 }}>
                  Amount
                </th>
                <th style={{ width: 140 }}>CreditOrDebit</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-muted">
                    No statement lines
                  </td>
                </tr>
              ) : (
                lines.map((e) => {
                  const checked = selectedIds.has(e.id);
                  const matched = isRowMatched(e.id);
                  const side = (e.creditOrDebit || e.debitCredit || "")
                    .toString()
                    .trim()
                    .toLowerCase();

                  return (
                    <tr key={e.id} className={matched ? "table-success" : ""}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggle && onToggle(e.id)}
                          disabled={matched}
                          title={matched ? "Already matched" : "Select"}
                        />
                      </td>
                      <td>{this.formatDate(e.date || e.txnDate)}</td>
                      <td>{e.particulars}</td>
                      <td className="text-end">
                        {this.formatMoney(Number(e.amount || 0))}
                      </td>
                      <td>
                        {side ? (
                          <Badge bg={side === "credit" ? "success" : "danger"}>
                            {side.charAt(0).toUpperCase() + side.slice(1)}
                          </Badge>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between">
          <div className="text-muted">
            {(lines || []).length} item{(lines || []).length === 1 ? "" : "s"}
          </div>
          <div className="text-end">
            <span className="me-3">
              <strong>Cr:</strong> {this.formatMoney(totals.cr)}
            </span>
            <span className="me-3">
              <strong>Dr:</strong> {this.formatMoney(totals.dr)}
            </span>
            <span>
              <strong>Net:</strong> {this.formatMoney(net)}
            </span>
          </div>
        </Card.Footer>
      </Card>
    );
  }
}
